import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  Check,
  CheckCircle2,
  CircleDollarSign,
  Info,
  RotateCw,
  Send,
  ShieldAlert,
} from "lucide-react";

import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { orgScopedPath } from "@/lib/orgDoor";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { EmptyPanel, PageHero, PagePanel, PageShell } from "@/components/interior-page";
import {
  fmtDate,
  loadFundingPartners,
  refreshFundingPartners,
  statusLabel,
  type FundingApplication,
  type FundingPartner,
  type PartnerField,
} from "@/lib/funding";
import { cn } from "@/lib/utils";

/* One funding company's pre-qualification form.
   Nothing here is specific to any company — the questions, their order, their
   types and their required flags all come from GET /funding/partners, which is
   generated from app/services/funding_partners.py. Onboarding a new company is
   a backend-only edit; this page renders whatever it is handed.

   ?listing=<id> names the deal being financed; it is sent as listing_id and
   lands in the lender's alert email. */

type Answers = Record<string, string>;

// An inquiry to the same lender within this window gets a "you already applied"
// warning — a second packet is usually a duplicate, not a new deal.
const RECENT_DAYS = 30;

// Matches MAX_MONEY / MAX_LINE / MAX_TEXT in app/services/funding_partners.py.
const MAX_MONEY = 999_999_999;
const MAX_LINE = 500;
const MAX_TEXT = 5000;

const textareaClass =
  "w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-3 text-sm leading-relaxed transition-all placeholder:text-muted-foreground/70 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25";

/**
 * How wide one question sits in the two-column grid.
 *
 * The page runs full width, so a single stacked column would stretch every
 * input across the whole monitor. Short answers pair up two to a row.
 *
 * Textareas, radio groups and consent boxes take the whole row instead — not
 * for the room, but so rows stay level. A three-option radio is roughly three
 * times the height of a text input, and pairing the two left a stack of ragged
 * half-empty cells. Across a full row the radio's options lay out side by side
 * (see RadioGroup) and the row is one input tall again.
 */
function spanFor(field: PartnerField): string {
  return field.type === "textarea" || field.type === "radio" || field.type === "consent" ? "lg:col-span-2" : "";
}

/** Option columns for a full-row radio. Static class names — Tailwind cannot
    see a template literal, so these have to be spelled out. */
const RADIO_COLUMNS: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
};

function Question({
  field,
  error,
  className,
  children,
}: {
  field: PartnerField;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  // A consent box carries its statement beside the tick, not above it.
  if (field.type === "consent") {
    return (
      <div data-invalid={!!error} className={className}>
        {children}
        {error && <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    );
  }

  return (
    <div data-invalid={!!error} className={className}>
      <label className="mb-2 block text-sm font-semibold leading-6 text-foreground">
        {field.label}
        {field.required && <span className="ml-1 text-primary">*</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">{error}</p>
      ) : field.help ? (
        <p className="mt-1.5 text-xs text-muted-foreground">{field.help}</p>
      ) : null}
    </div>
  );
}

/** Radio groups render as cards, not native dots — legible on the dark theme
    and a real hit target on mobile. */
function RadioGroup({
  field,
  value,
  onChange,
  invalid,
}: {
  field: PartnerField;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  // Options sit side by side from sm up, stacked on mobile. Four or more fall
  // back to three columns and wrap rather than shrinking into unreadable slivers.
  const columns = RADIO_COLUMNS[field.options.length] ?? "sm:grid-cols-3";

  return (
    <div role="radiogroup" aria-label={field.label} className={cn("grid grid-cols-1 gap-2", columns)}>
      {field.options.map((o) => {
        const on = value === o.value;
        return (
          <label
            key={o.value}
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors",
              on
                ? "border-primary/55 bg-primary/8"
                : invalid
                  ? "border-rose-500/50 bg-rose-500/5 hover:border-primary/35"
                  : "border-border/75 bg-card hover:border-primary/35",
            )}
          >
            <input
              type="radio"
              name={field.key}
              value={o.value}
              checked={on}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            <span
              className={cn(
                "flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                on ? "border-primary" : "border-border/80",
              )}
            >
              {on && <span className="h-2 w-2 rounded-full bg-primary" />}
            </span>
            <span className={cn("text-sm", on ? "font-semibold text-foreground" : "text-foreground/85")}>
              {o.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** A required agreement. Stored as "true" in the form state, sent as such. */
function ConsentBox({
  field,
  value,
  onChange,
  invalid,
}: {
  field: PartnerField;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  const on = value === "true";
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 transition-colors",
        on
          ? "border-primary/55 bg-primary/8"
          : invalid
            ? "border-rose-500/50 bg-rose-500/5"
            : "border-border/75 bg-card hover:border-primary/35",
      )}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked ? "true" : "")}
        className="sr-only"
      />
      <span
        className={cn(
          "mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border-2 transition-colors",
          on ? "border-primary bg-primary text-primary-foreground" : "border-border/80",
        )}
      >
        {on && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span className="text-sm leading-6 text-foreground/90">
        {field.label}
        {field.required && <span className="ml-1 text-primary">*</span>}
      </span>
    </label>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  invalid,
}: {
  field: PartnerField;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
}) {
  const ring = invalid ? "border-rose-500/60" : undefined;

  switch (field.type) {
    case "consent":
      return <ConsentBox field={field} value={value} onChange={onChange} invalid={invalid} />;

    case "radio":
      return <RadioGroup field={field} value={value} onChange={onChange} invalid={invalid} />;

    case "textarea":
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          maxLength={MAX_TEXT}
          placeholder={field.placeholder ?? undefined}
          className={cn(textareaClass, ring)}
        />
      );

    case "money":
      return (
        <div className="relative">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            $
          </span>
          <Input
            type="number"
            min={0}
            step={1000}
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? undefined}
            className={cn("pl-7", ring)}
          />
        </div>
      );

    case "number":
      return (
        <Input
          type="number"
          step={1}
          inputMode="numeric"
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={cn(ring)}
        />
      );

    default:
      return (
        <Input
          type={field.type === "url" ? "text" : field.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={MAX_LINE}
          placeholder={field.placeholder ?? undefined}
          autoComplete={
            field.role === "name" ? "name" : field.role === "email" ? "email" : field.role === "phone" ? "tel" : undefined
          }
          className={cn(ring)}
        />
      );
  }
}

/** Client-side mirror of funding_partners._coerce. The server validates too —
    this is only so the buyer is told before a round trip. */
function validate(fields: PartnerField[], answers: Answers): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const f of fields) {
    const raw = (answers[f.key] ?? "").trim();

    if (!raw) {
      if (f.required)
        errors[f.key] =
          f.type === "radio" ? "Choose one" : f.type === "consent" ? "Please confirm to continue" : "Required";
      continue;
    }

    if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      errors[f.key] = "Enter a valid email address";
    } else if (f.type === "radio" && !f.options.some((o) => o.value === raw)) {
      errors[f.key] = "Choose one of the listed options";
    } else if (f.type === "money") {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) errors[f.key] = "Enter a valid amount";
      else if (n > MAX_MONEY) errors[f.key] = "That looks like a typo";
    } else if (f.type === "number") {
      const n = Number(raw);
      if (!Number.isInteger(n)) errors[f.key] = "Enter a whole number";
      else if (f.min !== null && n < f.min) errors[f.key] = `Must be at least ${f.min}`;
      else if (f.max !== null && n > f.max) errors[f.key] = `Must be at most ${f.max}`;
    }
  }

  return errors;
}

export default function FundingApplyPage() {
  const { partnerSlug } = useParams<{ partnerSlug: string }>();
  const [searchParams] = useSearchParams();
  const listingId = searchParams.get("listing");
  const { user } = useAuth();
  const toast = useToast();

  const [partner, setPartner] = useState<FundingPartner | null | "missing" | "error">(null);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  const [answers, setAnswers] = useState<Answers>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ reference_code: string; partner_name: string } | null>(null);
  // Errors only render after a submit attempt, so an untouched form is not a
  // wall of red on first paint.
  const [showErrors, setShowErrors] = useState(false);
  // The deal this inquiry is for, when the buyer came from a listing.
  const [listing, setListing] = useState<{ id: string; title: string } | null>(null);
  // Earlier inquiries to this same lender, for the duplicate warning.
  const [previous, setPrevious] = useState<FundingApplication[]>([]);

  // Load the company, then seed the roled answers from the signed-in account
  // and every other answer from the field's default.
  useEffect(() => {
    let stale = false;
    setPartner(null);
    setDone(null);
    setShowErrors(false);
    loadFundingPartners()
      .then((list) => {
        if (stale) return;
        const found = list.find((p) => p.slug === partnerSlug);
        if (!found) {
          setPartner("missing");
          return;
        }
        setPartner(found);
        const seeded: Answers = {};
        for (const f of found.fields) {
          if (f.role === "name") seeded[f.key] = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
          else if (f.role === "email") seeded[f.key] = user?.email ?? "";
          else if (f.role === "phone") seeded[f.key] = user?.phone ?? "";
          else seeded[f.key] = f.default ?? "";
        }
        setAnswers(seeded);
      })
      .catch(() => {
        if (!stale) setPartner("error");
      });
    return () => {
      stale = true;
    };
  }, [partnerSlug, user, reloadKey]);

  // Refetched after a submit (done changes) so "Submit another" warns about
  // the one just sent.
  useEffect(() => {
    let stale = false;
    api
      .get<FundingApplication[]>("/funding/applications")
      .then((apps) => {
        if (!stale) setPrevious(apps.filter((a) => a.partner_slug === partnerSlug));
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [partnerSlug, done]);

  useEffect(() => {
    if (!listingId) {
      setListing(null);
      return;
    }
    let stale = false;
    api
      .get<{ id: string; title: string }>(`/business-listings/${listingId}`)
      .then((l) => {
        if (!stale) setListing({ id: l.id, title: l.title });
      })
      .catch(() => {
        if (!stale) setListing(null);
      });
    return () => {
      stale = true;
    };
  }, [listingId]);

  const recent = useMemo(() => {
    const cutoff = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
    return previous.find((a) => new Date(a.created_at).getTime() >= cutoff) ?? null;
  }, [previous]);

  const fields = partner && typeof partner === "object" ? partner.fields : [];
  const errors = useMemo(() => validate(fields, answers), [fields, answers]);
  const err = (key: string) => (showErrors ? errors[key] : undefined);

  /** True if the server's form has questions this page lacked (now added). */
  async function adoptNewQuestions(current: FundingPartner): Promise<boolean> {
    let fresh: FundingPartner | undefined;
    try {
      fresh = (await refreshFundingPartners()).find((p) => p.slug === current.slug);
    } catch {
      return false;
    }
    const known = new Set(current.fields.map((f) => f.key));
    const added = fresh?.fields.filter((f) => !known.has(f.key)) ?? [];
    if (!fresh || added.length === 0) return false;

    setPartner(fresh);
    setAnswers((a) => {
      const next = { ...a };
      for (const f of added) next[f.key] = f.default ?? "";
      return next;
    });
    setShowErrors(true);
    toast.error(
      added.length === 1
        ? "This form has a new question. Please answer it and submit again."
        : "This form has new questions. Please answer them and submit again.",
    );
    // After the re-render, bring the first new question into view.
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>("[data-invalid='true']")?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
    return true;
  }

  async function submit() {
    if (partner === null || typeof partner !== "object") return;
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      toast.error("Some required answers are missing.");
      // Put the first bad answer in front of them rather than making them hunt.
      document
        .querySelector<HTMLElement>("[data-invalid='true']")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      // Trim here so the server stores what the buyer meant, and drop blanks so
      // an untouched optional field is absent rather than "".
      const payload: Answers = {};
      for (const f of fields) {
        const v = (answers[f.key] ?? "").trim();
        if (v) payload[f.key] = v;
      }
      const res = await api.post<{ reference_code: string; partner_name: string }>(
        `/funding/partners/${partner.slug}/applications`,
        { answers: payload, listing_id: listing?.id ?? null },
      );
      setDone(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // A 422 for a question this page doesn't show means the form changed
      // after the tab loaded it. Pull the current questions in, keep every
      // answer already typed, and point the buyer at what's new.
      if (e instanceof ApiError && e.status === 422 && (await adoptNewQuestions(partner))) return;
      toast.error(
        e instanceof ApiError && e.status === 429
          ? "Too many submissions. Wait a minute and try again."
          : e instanceof ApiError
            ? e.message
            : "Could not submit the application. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (partner === null) {
    return (
      <PageShell>
        <div className="flex justify-center py-20">
          <Loader />
        </div>
      </PageShell>
    );
  }

  if (partner === "error") {
    return (
      <PageShell>
        <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-muted-foreground/60" />
          <p className="mt-3 font-bold text-foreground">Could not load this form</p>
          <p className="mt-1 text-sm text-muted-foreground">Check your connection and try again.</p>
          <Button type="button" variant="outline" className="mt-4 gap-2" onClick={retry}>
            <RotateCw className="h-4 w-4" /> Retry
          </Button>
        </div>
      </PageShell>
    );
  }

  if (partner === "missing") {
    return (
      <PageShell>
        <EmptyPanel
          icon={CircleDollarSign}
          title="Funding company not found"
          description="This company is no longer available. Pick one from the Funding Marketplace menu in the sidebar."
        />
      </PageShell>
    );
  }

  if (done) {
    return (
      <PageShell>
        <PagePanel className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/12 text-emerald-500">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-bold text-foreground">Application received</h1>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-7 text-muted-foreground">
            Your application has gone to <strong className="text-foreground">{done.partner_name}</strong>. Quote this
            reference when you speak to them:
          </p>
          <div className="mx-auto mt-5 inline-block rounded-xl border border-primary/30 bg-primary/10 px-5 py-3 font-mono text-xl font-bold tracking-wide text-primary">
            {done.reference_code}
          </div>
          <div className="mx-auto mt-6 max-w-lg rounded-xl border border-border/60 bg-muted/20 p-4 text-left">
            <p className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              What happens next
            </p>
            <p className="mt-1.5 text-sm leading-6 text-foreground/85">
              {partner.next_steps ??
                `${done.partner_name} reviews your answers and contacts you directly by email or phone. There is nothing else you need to do for now.`}
            </p>
          </div>
          <p className="mx-auto mt-5 max-w-lg text-sm leading-6 text-muted-foreground">
            You can check where it stands any time on the{" "}
            {/* Colour on the span: `a { color: inherit }` beats text-* on the Link. */}
            <Link to={orgScopedPath("/buyer/funding", user?.org_slug)}>
              <span className="font-semibold text-primary hover:underline">Funding Marketplace</span>
            </Link>
            .
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to={orgScopedPath("/buyer/funding", user?.org_slug)}>
              <Button type="button" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to lenders
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDone(null);
                setShowErrors(false);
              }}
            >
              Submit another application
            </Button>
          </div>
        </PagePanel>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* The company's own name is the page title — one page per company. */}
      <PageHero
        eyebrow="Funding Marketplace"
        title={partner.name}
        description={partner.tagline}
        icon={CircleDollarSign}
      />

      <PagePanel>
        {recent && (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm leading-6 text-foreground/90">
              You already sent {partner.name} an inquiry on {fmtDate(recent.created_at)} (
              <span className="font-mono font-semibold">{recent.reference_code}</span>,{" "}
              {statusLabel(recent.status).toLowerCase()}). Only submit again for a different deal or if your situation
              has changed. To update the earlier one, quote its reference when {partner.name} contacts you.
            </p>
          </div>
        )}

        {listing && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/8 px-4 py-3">
            <Briefcase className="h-4 w-4 shrink-0 text-primary" />
            <p className="min-w-0 text-sm text-foreground">
              This inquiry is for <strong className="font-semibold">{listing.title}</strong>. The deal is sent with
              your answers.
            </p>
          </div>
        )}

        <p className="mb-6 text-sm leading-6 text-muted-foreground">{partner.intro}</p>

        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          noValidate
        >
          {/* items-start so a tall radio group does not pad out the short
              input beside it — each cell keeps its natural height. */}
          <div className="grid grid-cols-1 items-start gap-x-8 gap-y-6 lg:grid-cols-2">
            {fields.map((f) => (
              <Question key={f.key} field={f} error={err(f.key)} className={spanFor(f)}>
                <FieldControl
                  field={f}
                  value={answers[f.key] ?? ""}
                  onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}
                  invalid={!!err(f.key)}
                />
              </Question>
            ))}
          </div>

          <div className="border-t border-border/60 pt-5">
            <Button type="submit" disabled={submitting} size="lg" className="w-full gap-2 sm:w-auto">
              {submitting ? "Submitting…" : "Submit"}
              {!submitting && <Send className="h-4 w-4" />}
            </Button>

            {fields.some((f) => f.required) && (
              <p className="mt-4 flex items-start gap-2 text-xs leading-6 text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Required fields are marked with <span className="font-semibold text-primary">*</span>
              </p>
            )}
            {partner.footnote && (
              <p className="mt-1.5 flex items-start gap-2 text-xs leading-6 text-muted-foreground">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {partner.footnote}
              </p>
            )}
          </div>
        </form>
      </PagePanel>
    </PageShell>
  );
}
